/**
 * **The page builder's wire** — how the console's editor and the guest site's
 * preview mode talk across an iframe (`docs/console-redesign-plan.md` §3, C).
 *
 * The canvas is the real site: `apps/site` loaded at `PREVIEW_HASH` inside an
 * iframe, rendering a *draft* branding document it was handed by `postMessage`
 * with the business's real catalog, hours and photographs. The editor
 * (`apps/admin`) owns the document, the undo stack and every write; the canvas
 * owns nothing but the picture and reports what the owner pointed at. Every
 * message in either direction is one of the shapes below and nothing else.
 *
 * ## Why here, and why no zod
 *
 * Both apps must agree about these shapes letter for letter, which is this
 * package's whole job — and this package is pure: no zod, no DOM, no I/O
 * (`./index.ts`). So the parsers are written by hand, as the rest of the
 * contract's readers are. They are **total**: `parseEditorMessage` and
 * `parseCanvasMessage` take anything a `message` event can carry and answer a
 * typed message or `null`, never throw, and never return an object that shares
 * structure with the input except the one field that is meant to be opaque
 * (`draft.branding`, which the site's own tolerant reader parses again).
 *
 * ## The envelope
 *
 * Every message carries `source: PREVIEW_SOURCE` and `v:
 * PREVIEW_PROTOCOL_VERSION`. A message without both is somebody else's traffic
 * (a browser extension, a devtools panel, a third-party widget on the console)
 * and is ignored rather than answered; a message with the source and another
 * version is a console one deploy away from this bundle, and the canvas says
 * so with an `error` rather than guessing.
 *
 * ## Origins
 *
 * `postMessage` is only as safe as the two origin checks around it, so both
 * sides make them, and neither ever uses `*`:
 *
 *  - the **canvas** accepts a message only from an allow-listed parent origin
 *    (`isAllowedParentOrigin`) *and* only from `window.parent`, and posts only
 *    to that origin. Without the first check any page on the internet could
 *    frame a tenant's real hostname and paint words on it; the draft would be
 *    rendered under the tenant's own address bar.
 *  - the **editor** accepts a message only from the iframe's own
 *    `contentWindow` and the origin it loaded it from, and posts only there.
 */
import { CATEGORY_SLUG_PATTERN, ITEM_ID_PATTERN } from './pages.js';
import { CONTENT_PAGE_SLUG_PATTERN } from './content.js';
import { RICHTEXT_MAX } from './sections.js';
/** Bumped when a message changes shape. Additive fields do not bump it. */
export const PREVIEW_PROTOCOL_VERSION = 1;
/** The envelope tag every message carries. */
export const PREVIEW_SOURCE = 'yourstore-preview';
/** The canvas's address (`/#/__preview`) — see `./preview-address.ts`. */
export { PREVIEW_HASH, isPreviewHash } from './preview-address.js';
export const PREVIEW_DEVICES = ['desktop', 'tablet', 'phone'];
/** The console's two languages. The canvas's own few words follow it. */
export const PREVIEW_UI_LOCALES = ['en', 'es'];
const SECTION_ACTIONS = ['up', 'down', 'duplicate', 'remove'];
const ERROR_CODES = [
    'bad-message',
    'version-mismatch',
    'data-unavailable',
    'render-failed',
];
/** Put the envelope on a message. Both sides post only what this returns. */
export function envelope(message) {
    return { ...message, source: PREVIEW_SOURCE, v: PREVIEW_PROTOCOL_VERSION };
}
/* ------------------------------------------------------------------ */
/* Parsing                                                             */
/* ------------------------------------------------------------------ */
/** Limits on what a message may carry. Generous; they exist to bound junk. */
const KEY_MAX = 64;
const SLOT_NAME = /^[a-z][a-z0-9_]{0,39}$/;
const EDIT_ID = /^[A-Za-z0-9_-]{1,64}$/;
const LABEL_MAX = 80;
const LABELS_MAX = 64;
const HREF_MAX = 2_000;
const MESSAGE_MAX = 400;
const SECTIONS_MAX = 200;
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isPlainRecord(value) {
    if (!isRecord(value))
        return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}
function str(value, max) {
    return typeof value === 'string' && value.length <= max;
}
function key(value) {
    return str(value, KEY_MAX) && value !== '';
}
function nat(value) {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}
function finite(value) {
    return typeof value === 'number' && Number.isFinite(value);
}
function oneOf(value, options) {
    return typeof value === 'string' && options.includes(value);
}
/** A `PreviewPage` out of anything, or `null`. A fresh object either way. */
export function readPreviewPage(value) {
    if (!isRecord(value))
        return null;
    switch (value['kind']) {
        case 'home':
            return { kind: 'home' };
        case 'content':
            return str(value['slug'], 80) && CONTENT_PAGE_SLUG_PATTERN.test(value['slug'])
                ? { kind: 'content', slug: value['slug'] }
                : null;
        case 'category':
            return str(value['slug'], 80) && CATEGORY_SLUG_PATTERN.test(value['slug'])
                ? { kind: 'category', slug: value['slug'] }
                : null;
        case 'item':
            return typeof value['item_id'] === 'string' &&
                new RegExp(ITEM_ID_PATTERN).test(value['item_id'])
                ? { kind: 'item', item_id: value['item_id'] }
                : null;
        default:
            return null;
    }
}
function readRect(value) {
    if (!isRecord(value))
        return null;
    const { x, y, width, height } = value;
    return finite(x) && finite(y) && finite(width) && finite(height) && width >= 0 && height >= 0
        ? { x, y, width, height }
        : null;
}
function readLabels(value) {
    if (!isPlainRecord(value))
        return null;
    const entries = Object.entries(value);
    if (entries.length > LABELS_MAX)
        return null;
    const out = {};
    for (const [name, label] of entries) {
        if (!SLOT_NAME.test(name) || !str(label, LABEL_MAX))
            return null;
        out[name] = label;
    }
    return out;
}
/** The envelope, checked. `null` means "not ours"; a string is a refusal. */
function open(data) {
    if (!isRecord(data) || data['source'] !== PREVIEW_SOURCE)
        return 'foreign';
    if (data['v'] !== PREVIEW_PROTOCOL_VERSION)
        return 'version-mismatch';
    return data;
}
function readEditor(data) {
    switch (data['type']) {
        case 'hello':
            return { type: 'hello' };
        case 'draft': {
            const page = readPreviewPage(data['page']);
            if (!nat(data['rev']) || !isPlainRecord(data['branding']) || page === null)
                return null;
            const out = {
                type: 'draft',
                rev: data['rev'],
                branding: data['branding'],
                page,
            };
            if (data['device'] !== undefined) {
                if (!oneOf(data['device'], PREVIEW_DEVICES))
                    return null;
                out.device = data['device'];
            }
            if (data['ui_locale'] !== undefined) {
                if (!oneOf(data['ui_locale'], PREVIEW_UI_LOCALES))
                    return null;
                out.ui_locale = data['ui_locale'];
            }
            if (data['labels'] !== undefined) {
                const labels = readLabels(data['labels']);
                if (labels === null)
                    return null;
                out.labels = labels;
            }
            if (data['selected'] !== undefined) {
                if (data['selected'] !== null && !key(data['selected']))
                    return null;
                out.selected = data['selected'];
            }
            return out;
        }
        case 'select':
        case 'hover':
            return data['key'] === null || key(data['key'])
                ? { type: data['type'], key: data['key'] }
                : null;
        case 'scroll-to': {
            if (!key(data['key']))
                return null;
            const out = { type: 'scroll-to', key: data['key'] };
            if (data['behavior'] !== undefined) {
                if (!oneOf(data['behavior'], ['smooth', 'instant']))
                    return null;
                out.behavior = data['behavior'];
            }
            return out;
        }
        case 'edit-text-commit-ack': {
            const id = data['edit_id'];
            if (typeof id !== 'string' || !EDIT_ID.test(id) || typeof data['accepted'] !== 'boolean') {
                return null;
            }
            const out = {
                type: 'edit-text-commit-ack',
                edit_id: id,
                accepted: data['accepted'],
            };
            if (data['message'] !== undefined) {
                if (!str(data['message'], MESSAGE_MAX))
                    return null;
                out.message = data['message'];
            }
            return out;
        }
        default:
            return null;
    }
}
function readCanvas(data) {
    switch (data['type']) {
        case 'ready':
            return nat(data['version']) ? { type: 'ready', version: data['version'] } : null;
        case 'section-rects': {
            const raw = data['sections'];
            const viewport = data['viewport'];
            if (!nat(data['rev']) || !Array.isArray(raw) || raw.length > SECTIONS_MAX)
                return null;
            if (!isRecord(viewport))
                return null;
            const { scroll_y, width, height, doc_height } = viewport;
            if (!finite(scroll_y) || !finite(width) || !finite(height) || !finite(doc_height)) {
                return null;
            }
            const sections = [];
            for (const entry of raw) {
                if (!isRecord(entry))
                    return null;
                const rect = readRect(entry['rect']);
                if (rect === null ||
                    !key(entry['key']) ||
                    !str(entry['type'], KEY_MAX) ||
                    !str(entry['variant'], KEY_MAX) ||
                    !nat(entry['index'])) {
                    return null;
                }
                sections.push({
                    key: entry['key'],
                    type: entry['type'],
                    variant: entry['variant'],
                    index: entry['index'],
                    rect,
                });
            }
            return {
                type: 'section-rects',
                rev: data['rev'],
                sections,
                viewport: { scroll_y, width, height, doc_height },
            };
        }
        case 'select':
            return key(data['key']) && nat(data['index'])
                ? { type: 'select', key: data['key'], index: data['index'] }
                : null;
        case 'hover':
            return data['key'] === null || key(data['key']) ? { type: 'hover', key: data['key'] } : null;
        case 'insert-at':
            return nat(data['index']) ? { type: 'insert-at', index: data['index'] } : null;
        case 'section-action':
            return key(data['key']) && nat(data['index']) && oneOf(data['action'], SECTION_ACTIONS)
                ? { type: 'section-action', key: data['key'], index: data['index'], action: data['action'] }
                : null;
        case 'text-edit': {
            const { edit_id, rev, index, slot, text } = data;
            return typeof edit_id === 'string' &&
                EDIT_ID.test(edit_id) &&
                nat(rev) &&
                key(data['key']) &&
                nat(index) &&
                typeof slot === 'string' &&
                SLOT_NAME.test(slot) &&
                str(text, RICHTEXT_MAX)
                ? { type: 'text-edit', edit_id, rev, key: data['key'], index, slot, text }
                : null;
        }
        case 'nav': {
            if (!str(data['href'], HREF_MAX) || typeof data['external'] !== 'boolean')
                return null;
            const out = { type: 'nav', href: data['href'], external: data['external'] };
            if (data['page'] !== undefined) {
                const page = readPreviewPage(data['page']);
                if (page === null)
                    return null;
                out.page = page;
            }
            if (data['anchor'] !== undefined) {
                if (!str(data['anchor'], HREF_MAX))
                    return null;
                out.anchor = data['anchor'];
            }
            return out;
        }
        case 'error':
            return oneOf(data['code'], ERROR_CODES) && str(data['message'], MESSAGE_MAX)
                ? { type: 'error', code: data['code'], message: data['message'] }
                : null;
        default:
            return null;
    }
}
function parse(data, read) {
    // Structured clone strips getters and prototypes, so a message that arrived
    // by `postMessage` cannot throw here; a caller handing an object in directly
    // can, and "never throws" is a promise about every caller.
    try {
        const opened = open(data);
        if (typeof opened === 'string')
            return { ok: false, refusal: opened };
        const message = read(opened);
        return message === null ? { ok: false, refusal: 'bad-message' } : { ok: true, message };
    }
    catch {
        return { ok: false, refusal: 'bad-message' };
    }
}
/** Read what the canvas received. Never throws. */
export function parseEditorMessage(data) {
    return parse(data, readEditor);
}
/** Read what the editor received. Never throws. */
export function parseCanvasMessage(data) {
    return parse(data, readCanvas);
}
/* ------------------------------------------------------------------ */
/* Origins                                                             */
/* ------------------------------------------------------------------ */
/**
 * The consoles that may drive a canvas, in every deployment. Exact origins —
 * scheme, host and port — compared as strings after `URL` normalises them.
 * An environment with another console adds it at build time
 * (`VITE_PREVIEW_PARENT_ORIGINS` on `apps/site`, dev.md §5).
 */
export const PREVIEW_PARENT_ORIGINS = [
    'https://admin.yourstore.jswaves.com',
    'https://admin-test.yourstore.jswaves.com',
];
/** `scheme://host[:port]` of a URL, or `null` for anything that is not one. */
export function originOf(url) {
    try {
        const parsed = new URL(url);
        return parsed.origin === 'null' ? null : parsed.origin;
    }
    catch {
        return null;
    }
}
/** A loopback development origin: `http://localhost:<port>`, `127.0.0.1`, `*.localhost`. */
export function isLocalOrigin(origin) {
    let parsed;
    try {
        parsed = new URL(origin);
    }
    catch {
        return false;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')
        return false;
    const host = parsed.hostname;
    return (host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host.endsWith('.localhost'));
}
/**
 * May a page at `origin` drive this canvas?
 *
 * `allowLocal` is the development allowance — any loopback port, because the
 * console's dev server and the site's are two ports on one laptop and neither
 * is fixed. The site grants it only when the site itself is being served from
 * a loopback host or a dev build, so a deployed tenant hostname never accepts
 * a parent on somebody's `localhost`.
 */
export function isAllowedParentOrigin(origin, allowed, allowLocal) {
    const normalised = originOf(origin);
    if (normalised === null)
        return false;
    if (allowed.some((entry) => originOf(entry) === normalised))
        return true;
    return allowLocal && isLocalOrigin(normalised);
}
//# sourceMappingURL=preview.js.map